import React, { useRef, useMemo } from "react";
import { useState, useEffect } from "react";
import moment from "moment";
import { useRecoilState } from "recoil";

import JournalEvents from "../components/JournalEvents";
import { fetchEventsCategories } from "../HTTPS/ApiService";
import { usePatient } from "../context/PatientContext";
import useEventManagement from "../hooks/useEventManagement";
import MessengerService from "../servies/MessengerService";
import MainAreaWrapper from "../layout/MainAreaWrapper";
import SearchBar from "../components/UI/SearchBar";
import { useUser } from "../context/UserContext";
import ReuseableBlurModel from "../components/Modals/ReuseableBlurModel";
import EditActivityForm from "../components/Forms/EditActivityForm";
import AddActivityForm from "../components/Forms/AddActivityForm";
import useReactQuery from "../hooks/useReactQuery";
import { getCorrectDateTimeString } from "../utils/chartUtils";
import ObservationBlock from "../components/Blocks/ObservationBlock";
import { IconButton, Tooltip, Alert } from "@mui/material";
import QuickJournalingComp from "../components/Blocks/QuickJournalingComp";
import QuickJournalingVoiceInputComp from "../components/Blocks/QuickJournalingVoiceInputComp";
import { activeTabState } from "../recoil/journel/activeTabSate";
import { useQueryClient } from "@tanstack/react-query";

const Journal = () => {
  const observationsRef = useRef(null); // Add the ref here
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [active, setActive] = useRecoilState(activeTabState);
  const queryClient = useQueryClient();

  const { data: categories } = useReactQuery(
    "EventsCategories",
    fetchEventsCategories,
  );

  const [quickJournalingOpen, setQuickJournalingOpen] = useState(false);
  const [quickJournalingVoiceInputOpen, setQuickJournalingVoiceInputOpen] = useState(false);
  const [voiceInputError, setVoiceInputError] = useState(null); // { type: 'error'|'warning', message: string }
  const [createActivityOpen, setCreateActivityOpen] = useState(false);
  const [editActivityOpen, setEditActivityOpen] = useState(false);
  const [activityToBeEdited, setActivityToBeEdited] = useState({});
  const { patient } = usePatient();
  const voiceInputCloseCallbackRef = useRef(null);

  const toggleActive = (index) => {
    setActive(index);
  };

  const { user } = useUser();

  useEffect(() => {
    if (patient) {
      MessengerService.setSelectedPatient(patient.id);
      localStorage.setItem("selectedPatient", JSON.stringify(patient));
    }
  }, [patient]);

  const { event, events, deleteEventHandler, fetchEvents } =
    useEventManagement();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterEventType, setFilterEventType] = useState("past");
  const [customDateRange, setCustomDateRange] = useState({ fromDate: '', toDate: '' });
  const [debouncedDateRange, setDebouncedDateRange] = useState({ fromDate: '', toDate: '' });
  const [isFetchingEvents, setIsFetchingEvents] = useState(false);
  const dateRangeTimeoutRef = useRef(null);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Debounce date range changes to prevent multiple API calls
  useEffect(() => {
    if (dateRangeTimeoutRef.current) {
      clearTimeout(dateRangeTimeoutRef.current);
    }

    dateRangeTimeoutRef.current = setTimeout(() => {
      setDebouncedDateRange(customDateRange);
    }, 800); // Wait 800ms after user stops selecting dates

    return () => {
      if (dateRangeTimeoutRef.current) {
        clearTimeout(dateRangeTimeoutRef.current);
      }
    };
  }, [customDateRange]);

  // Calculate date range based on filter type or custom range and fetch events
  useEffect(() => {
    if (!patient?.id) return;

    setIsFetchingEvents(true);
    let fromDate = null;
    let toDate = null;

    // If custom date range is set, use it
    if (debouncedDateRange.fromDate || debouncedDateRange.toDate) {
      fromDate = debouncedDateRange.fromDate || null;
      toDate = debouncedDateRange.toDate || null;
    } else if (filterEventType !== "isImp") {
      const today = moment();
      toDate = today.format("YYYY-MM-DD");

      if (filterEventType === "today") {
        fromDate = today.format("YYYY-MM-DD");
      } else if (filterEventType === "yesterday") {
        const yesterday = today.clone().subtract(1, "day");
        fromDate = yesterday.format("YYYY-MM-DD");
      } else if (filterEventType === "past") {
        const sevenDaysAgo = today.clone().subtract(7, "days");
        fromDate = sevenDaysAgo.format("YYYY-MM-DD");
      }
    }

    fetchEvents(fromDate, toDate).finally(() => {
      setIsFetchingEvents(false);
      setIsInitialLoad(false);
    });
  }, [filterEventType, debouncedDateRange, patient?.id, fetchEvents]);

  // Filter events by search term and important flag (date filtering is now done by API)
  const filteredEvents = useMemo(() => {
    if (filterEventType === "isImp") {
      return events.filter((item) => item?.isImp === true);
    }
    
    // Only filter by search term, date filtering is handled by API
    return events.filter((item) =>
      item.text.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [events, filterEventType, searchTerm]);

  const handleEventFilterClick = (filterType) => {
    setFilterEventType(filterType);
  };

  const handleClearFilter = () => {
    setSearchTerm("");
    setFilterEventType("all");
    setCustomDateRange({ fromDate: '', toDate: '' });
  };

  const handleDateRangeChange = (newRange) => {
    setCustomDateRange(newRange);
    // Clear preset filter when using custom date range
    if (newRange.fromDate || newRange.toDate) {
      setFilterEventType('');
    }
  };

  // Listen for token refresh events and invalidate queries + refetch events
  useEffect(() => {
    const handleTokenRefresh = () => {
      // Invalidate all React Query cache to force refetch with new token
      queryClient.invalidateQueries();
      
      // Also refetch events manually since they don't use React Query
      if (patient?.id) {
        let fromDate = null;
        let toDate = null;

        // Calculate current date range
        if (debouncedDateRange.fromDate || debouncedDateRange.toDate) {
          fromDate = debouncedDateRange.fromDate || null;
          toDate = debouncedDateRange.toDate || null;
        } else if (filterEventType !== "isImp") {
          const today = moment();
          toDate = today.format("YYYY-MM-DD");

          if (filterEventType === "today") {
            fromDate = today.format("YYYY-MM-DD");
          } else if (filterEventType === "yesterday") {
            const yesterday = today.clone().subtract(1, "day");
            fromDate = yesterday.format("YYYY-MM-DD");
          } else if (filterEventType === "past") {
            const sevenDaysAgo = today.clone().subtract(7, "days");
            fromDate = sevenDaysAgo.format("YYYY-MM-DD");
          }
        }

        fetchEvents(fromDate, toDate);
      }
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
    };
  }, [patient?.id, filterEventType, debouncedDateRange, fetchEvents, queryClient]);
  return (
    <MainAreaWrapper observationsRef={observationsRef}>
      <main className="bg-white mt-28 lg:mt-10 rounded-t-3xl pb-10 lg:pb-0 -z-0">
        {/* Message Bar  */}
        <div className="mx-auto p-4 md:p-6 2xl:p-10">
          <div className="mx-auto">
            {/* Voice Input Error Alert - Displayed prominently above title */}
            {voiceInputError && (
              <Alert
                severity={voiceInputError.type}
                onClose={() => setVoiceInputError(null)}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  boxShadow: voiceInputError.type === 'error' 
                    ? '0 4px 12px rgba(211, 47, 47, 0.3)' 
                    : '0 4px 12px rgba(237, 108, 2, 0.3)',
                  border: voiceInputError.type === 'error' 
                    ? '2px solid #d32f2f' 
                    : '2px solid #ed6c02',
                  '& .MuiAlert-icon': {
                    fontSize: { xs: 24, sm: 28 },
                  },
                }}
                className="animate-bounce"
              >
                {voiceInputError.message}
              </Alert>
            )}
            
            {/* <!-- Breadcrumb Start --> */}
            <h2 className="text-3xl sm:text-5xl font-bold text-[#1D3E7D]">
              Journaling
              <Tooltip title="Quick Journaling">
                <IconButton
                  aria-label="delete"
                  sx={{ ml: 2 }}
                  onClick={() => setQuickJournalingOpen(true)}
                >
                  <img
                    src="./icons/quick-journo.svg"
                    alt="quick-journo"
                    className="w-10 h-10 sm:w-12 sm:h-12"
                  />
                </IconButton>
              </Tooltip>

              {/* {(patient?.id === 1 || patient?.id === 5045 || patient?.id === 6045) && ( */}
                <Tooltip title="Voice Input">
                  <IconButton
                    aria-label="voice-input"
                    sx={{ ml: 2 }}
                    onClick={() => {
                      setVoiceInputError(null); // Clear any previous errors
                      setQuickJournalingVoiceInputOpen(true);
                    }}
                  >
                    <img
                      src="./icons/quick-journo-voice.svg"
                      alt="quick-journo-voice"
                      className="w-10 h-10 sm:w-12 sm:h-12"
                    />
                  </IconButton>
                </Tooltip>
              {/* )} */}
            </h2>

            {/* Toggle Button  */}
            <div className="flex flex-row gap-3 sm:gap-5 justify-around items-center py-3">
              <button
                onClick={() => {
                  toggleActive(1);
                  setSearchTerm("");
                }}
                className={`font-bold text-[#1F456E] border-b-4 sm:border-b-8 border-transparent w-1/2 rounded-t-2xl py-2 sm:py-4 text-lg sm:text-2xl ${active === 1 ? "!border-[#1F456E] bg-[#ECF1F4] px-2" : ""
                  }`}
                setActive={true}
              >
                Observations
              </button>
              <button
                onClick={() => {
                  toggleActive(2);
                  setSearchTerm("");
                }}
                className={`font-bold text-[#1F456E] border-b-4 sm:border-b-8 border-transparent w-1/2 rounded-t-2xl py-2 sm:py-4 text-lg sm:text-2xl ${active === 2 ? "!border-[#1F456E] bg-[#ECF1F4]" : ""
                  }`}
              >
                Activities
              </button>
            </div>

            {/* Content - Notes and Events */}

            {active === 1 && (
              <>
                <ObservationBlock observationsRef={observationsRef} />
              </>
            )}

            {/* Events Tab  */}
            {active === 2 && (
              <>
                <div className="flex items-end justify-end space-y-3 xs:space-y-0">
                  <div className="flex items-center">
                    <SearchBar
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      handleSearch={handleSearch}
                      handleEventFilterClick={handleEventFilterClick}
                      handleClearFilter={handleClearFilter}
                      filterEventType={filterEventType}
                      dateRange={customDateRange}
                      onDateRangeChange={handleDateRangeChange}
                    />
                  </div>

                  <div className="flex sm:mb-0">
                    <button
                      onClick={() => setCreateActivityOpen(true)}
                      disabled={
                        user?.role !== "Kare Admin" &&
                        user?.role !== "Kare Giver"
                      }
                      className={`p-2 sm:p-3 ${user?.role !== "Kare Admin" &&
                        user?.role !== "Kare Giver"
                        ? "flex items-center gap-2 rounded-full bg-[#ED7117] opacity-30"
                        : "flex items-center gap-2 rounded-full bg-[#ED7117]"
                        }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-8 h-8 sm:w-11 sm:h-11"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M18 12.75H6c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h12c.41 0 .75.34.75.75s-.34.75-.75.75Z"
                          fill="#fff"
                        ></path>
                        <path
                          d="M12 18.75c-.41 0-.75-.34-.75-.75V6c0-.41.34-.75.75-.75s.75.34.75.75v12c0 .41-.34.75-.75.75Z"
                          fill="#fff"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                {/* Show loading indicator on initial load */}
                {isInitialLoad && isFetchingEvents ? (
                  <div className="flex items-center justify-center p-10">
                    <div className="flex flex-col items-center gap-3">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-blue-600"></div>
                      <span className="text-lg text-blue-700 font-medium">Loading activities...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Show subtle loading indicator while refetching but keep data visible */}
                    {isFetchingEvents && events.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-700">Updating activities...</span>
                      </div>
                    )}

                    {filteredEvents.length === 0 ? (
                      <p className="text-[#ff0000] p-5">
                        {isFetchingEvents ? 'Loading activities...' : 'No Activities found.'}
                      </p>
                    ) : (
                      <JournalEvents
                        key={event.id}
                        events={filteredEvents.sort((a, b) => {
                          const timestampA = getCorrectDateTimeString(
                            a.evDate,
                            a.evTime,
                          );
                          const timestampB = getCorrectDateTimeString(
                            b.evDate,
                            b.evTime,
                          );
                          if (moment(timestampA).isSame(timestampB)) {
                            return a.id > b.id ? -1 : 1; // because seconds are not there so we are comparing SQL id
                          } else {
                            return moment(timestampA).isBefore(timestampB)
                              ? 1
                              : -1;
                          }
                        })}
                        setEditActivityOpen={setEditActivityOpen}
                        setActivityToBeEdited={setActivityToBeEdited}
                        onDelete={(eventId) => deleteEventHandler(eventId)}
                        fetchEvents={fetchEvents}
                        categories={categories}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <ReuseableBlurModel
        open={createActivityOpen}
        setOpen={setCreateActivityOpen}
      >
        <AddActivityForm
          setOpen={setCreateActivityOpen}
          fetchEvents={fetchEvents}
        />
      </ReuseableBlurModel>
      <ReuseableBlurModel open={editActivityOpen} setOpen={setEditActivityOpen}>
        <EditActivityForm
          activity={activityToBeEdited}
          setOpen={setEditActivityOpen}
          fetchEvents={fetchEvents}
        />
      </ReuseableBlurModel>
      <ReuseableBlurModel
        open={quickJournalingOpen}
        setOpen={setQuickJournalingOpen}
        color="#000"
      >
        <div className="bg-white absolute right-0 bottom-0 rounded-xl py-4 px-2 h-screen mx-auto mt-10 w-full overflow-scroll pb-24">
          <QuickJournalingComp
            fetchEvents={fetchEvents}
            setQuickJournalingOpen={setQuickJournalingOpen}
          />
        </div>
      </ReuseableBlurModel>
      <ReuseableBlurModel
        open={quickJournalingVoiceInputOpen}
        setOpen={(value) => {
          // Call cancel recording callback if it exists before closing
          if (!value && voiceInputCloseCallbackRef.current) {
            voiceInputCloseCallbackRef.current();
          }
          setQuickJournalingVoiceInputOpen(value);
        }}
        color="#fff"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <QuickJournalingVoiceInputComp
              setQuickJournalingVoiceInputOpen={setQuickJournalingVoiceInputOpen}
              fetchEvents={fetchEvents}
              setVoiceInputError={setVoiceInputError}
              onClose={(callback) => {
                voiceInputCloseCallbackRef.current = callback;
              }}
            />
          </div>
        </div>
      </ReuseableBlurModel>
    </MainAreaWrapper>
  );
};

export default Journal;
